// A measure's status (docs/SCHEMA.md hig_measure): Measuring → Ready to assess →
// Assessing → Assessed → In review. A label for CRM lists, never a gate: nothing
// waits on it, and a measure nobody assesses goes straight to In review (DD,
// 2026-10-26). Taps set Ready to assess (with the day measured) and Assessed (with
// the day assessed); the app moves it on by itself only forwards. Pure.

/** hig_measurestatus, in CRM's order. Approved is unused: quote approval stays in CRM. */
const MEASURE_STATUSES = ['Measuring', 'Ready to assess', 'Assessing', 'Assessed', 'In review', 'Approved'] as const;
export type MeasureStatus = (typeof MEASURE_STATUSES)[number];

type Changes = Record<string, string>;

/** A status as stored; anything unknown (or none) reads as Measuring. */
export function statusOf(value: unknown): MeasureStatus {
  return MEASURE_STATUSES.includes(value as MeasureStatus) ? (value as MeasureStatus) : 'Measuring';
}

const rank = (value: unknown): number => MEASURE_STATUSES.indexOf(statusOf(value));

/** A step the app takes by itself (Assessing, In review): only ever forwards; null when already there or past it. */
export function advanceTo(current: unknown, target: MeasureStatus): Changes | null {
  return rank(target) > rank(current) ? { hig_status: target } : null;
}

/** "Measuring done": ready for the PM, with the day; the server records who. */
export const measuringDone = (today: string): Changes => ({ hig_status: 'Ready to assess', hig_measuredon: today });

/** "Back to measuring": the day measured stays as it was. */
export const backToMeasuring = (): Changes => ({ hig_status: 'Measuring' });

/** "Assessment done": Assessed (never back from In review), with the day; the server records who. */
export function assessmentDone(current: unknown, today: string): Changes {
  return { ...(advanceTo(current, 'Assessed') ?? {}), hig_assessedon: today };
}

export type StatusTap = 'measuring-done' | 'back-to-measuring' | 'assessment-done';

/** Which tap the iPad offers on each tab, if any. */
export function statusAction(current: unknown, tab: 'measure' | 'assess'): StatusTap | null {
  const status = statusOf(current);
  if (tab === 'assess') return rank(status) < rank('Assessed') ? 'assessment-done' : null;
  if (status === 'Measuring') return 'measuring-done';
  return status === 'Ready to assess' ? 'back-to-measuring' : null;
}

/**
 * A tap applied to the status stored now: null when it no longer applies (another device has moved the measure on
 * since the screen was drawn), so a stale tap never moves a status back.
 */
export function tapChanges(tap: StatusTap, stored: unknown, today: string): Changes | null {
  if (statusAction(stored, tap === 'assessment-done' ? 'assess' : 'measure') !== tap) return null;
  if (tap === 'measuring-done') return measuringDone(today);
  return tap === 'back-to-measuring' ? backToMeasuring() : assessmentDone(stored, today);
}
